import { Currency } from 'ui-toolkit';
import { IValidationResult, IWithId, Validator } from './interfaces';
import React from 'react';
import { IPercentageOfInterestViewItem } from '../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { TypeOfSubTypeEnum } from 'ui-toolkit/enums/eSubTypes';
import { eTaxAuthorityType } from 'ui-toolkit/enums/eTaxAuthorityType';
import { eTitleFees } from '../../ui-toolkit/enums/eTitleFees';
import { eUnderwriter } from '../../ui-toolkit/enums/eUnderwriter';
import { eEndorsements } from '../../ui-toolkit/enums/eEndorsements';
import { eCDV3ItemTypes } from '../../ui-toolkit/enums/eCDV3ItemTypes';
import { ePartyPurpose } from '../../ui-toolkit/enums/ePartyPurpose';
import { ePartyType } from '../../ui-toolkit/enums/ePartyType';
import { IOrderDetailsData } from '../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { isNil } from 'lodash';
import { sum } from '../../ui-toolkit/utilities/sum';

export function isNumber(val?: any | null): boolean {
  return !isNil(val) && !isNaN(val);
}

// Individual Properties
export const validateOptionalBuyerAtClosing: Validator<{ buyer_at_closing?: number | null }> = (item) => {
  const valid = isNil(item.buyer_at_closing) || isNumber(item.buyer_at_closing);
  return {
    id: `${item.id}-buyer_at_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateBuyerAtClosing: Validator<{ buyer_at_closing?: number | null }> = (item) => {
  const valid = isNumber(item.buyer_at_closing);
  return {
    id: `${item.id}-buyer_at_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalBuyerBeforeClosing: Validator<{ buyer_before_closing?: number | null }> = (item) => {
  const valid = isNil(item.buyer_before_closing) || isNumber(item.buyer_before_closing);
  return {
    id: `${item.id}-buyer_before_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateBuyerBeforeClosing: Validator<{ buyer_before_closing?: number | null }> = (item) => {
  const valid = isNumber(item.buyer_before_closing);
  return {
    id: `${item.id}-buyer_before_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateCreatedAt: Validator<{ created_at?: any }> = (item) => {
  const valid = typeof item.created_at === 'string' && !isNaN(new Date(item.created_at).getTime());
  return {
    id: `${item.id}-created_at`,
    message: `Created At is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateNettedOut: Validator<{ netted_out?: any }> = (item) => {
  const valid = typeof item.netted_out === 'boolean';
  return {
    id: `${item.id}-netted_out`,
    message: `Netted Out is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateId: Validator<{ id?: any }> = (item) => {
  const valid = typeof item.id === 'string';
  return {
    id: `${item.id}-id`,
    message: `Id is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOwnerId: Validator<{ owner_id?: any }> = (item) => {
  const valid = typeof item.owner_id === 'string';
  return {
    id: `${item.id}-owner_id`,
    message: `Owner Id is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalPaidByOthers: Validator<{ paid_by_others?: number | null }> = (item) => {
  const valid = isNil(item.paid_by_others) || isNumber(item.paid_by_others);
  return {
    id: `${item.id}-paid_by_others`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validatePaidByOthers: Validator<{ paid_by_others?: number | null }> = (item) => {
  const valid = isNumber(item.paid_by_others);
  return {
    id: `${item.id}-paid_by_others`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalSellerAtClosing: Validator<{ seller_at_closing?: number | null }> = (item) => {
  const valid = isNil(item.seller_at_closing) || isNumber(item.seller_at_closing);
  return {
    id: `${item.id}-seller_at_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateSellerAtClosing: Validator<{ seller_at_closing?: number | null }> = (item) => {
  const valid = isNumber(item.seller_at_closing);
  return {
    id: `${item.id}-seller_at_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalSellerBeforeClosing: Validator<{ seller_before_closing?: number | null }> = (item) => {
  const valid = isNil(item.seller_before_closing) || isNumber(item.seller_before_closing);
  return {
    id: `${item.id}-seller_before_closing`,
    message: `Amount is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateType: Validator<{ type?: eCDV3ItemTypes }> = (item) => {
  const valid = !!item.type && Object.values(eCDV3ItemTypes).includes(item.type);
  return {
    id: `${item.id}-type`,
    message: `Type is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

const validateSubType = (item: { sub_type?: any } & IWithId, type: TypeOfSubTypeEnum): IValidationResult => {
  const valid = !!item.sub_type && Object.values(type).includes(item.sub_type);
  return {
    id: `${item.id}-sub_type`,
    message: `Name is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const createValidateSubTypeFunction =
  (type: TypeOfSubTypeEnum): Validator<{ sub_type?: any }> =>
  (item) =>
    validateSubType(item, type);

export const validateShoppedFor: Validator<{ shopped_for?: boolean }> = (item) => {
  const valid = typeof item.shopped_for === 'boolean';
  return {
    id: `${item.id}-shopped_for`,
    message: `Shopped For is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOverridden: Validator<{ overridden?: boolean }> = (item) => {
  const valid = typeof item.overridden === 'boolean';
  return {
    id: `${item.id}-overridden`,
    message: `Overridden is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validatePayee: Validator<{ payee?: string | null }> = (item) => {
  const valid = typeof item.payee === 'string';
  return {
    id: `${item.id}-payee`,
    message: `Payee is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalPayee: Validator<{ payee?: string | null }> = (item) => {
  const valid = isNil(item.payee) || typeof item.payee === 'string';
  return {
    id: `${item.id}-payee`,
    message: `Payee is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalPercentage: Validator<{ percentage?: number | null }> = (item) => {
  const valid = isNil(item.percentage) || isNumber(item.percentage);
  return {
    id: `${item.id}-percentage`,
    message: `Percentage is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validatePercentage: Validator<{ percentage?: number | null }> = (item) => {
  const valid = isNumber(item.percentage);
  return {
    id: `${item.id}-percentage`,
    message: `Percentage is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validatePercentageOfInterest: Validator<IPercentageOfInterestViewItem, IOrderDetailsData> = (item) => {
  const itemIsEmpty =
    isNil(item.buyer_at_closing) &&
    isNil(item.buyer_before_closing) &&
    isNil(item.seller_at_closing) &&
    isNil(item.seller_before_closing) &&
    isNil(item.paid_by_others);
  return itemIsEmpty ? validateOptionalPercentage(item) : validatePercentage(item);
};

export const validateLabel: Validator<{ label?: string | null }> = (item) => {
  const valid = typeof item.label === 'string';
  return {
    id: `${item.id}-label`,
    message: `Name is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalLabel: Validator<{ label?: string | null }> = (item) => {
  const valid = isNil(item.label) || typeof item.label === 'string';
  return {
    id: `${item.id}-label`,
    message: `Name is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateEndorsement: Validator<{ type?: eCDV3ItemTypes; endorsement?: any }> = (item) => {
  const valid = !!item.endorsement && Object.values(eEndorsements).includes(item.endorsement);
  return {
    id: `${item.id}-endorsement`,
    message: `Endorsement is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateName: Validator<{ name?: string | null }> = (item) => {
  const valid = typeof item.name === 'string';
  return {
    id: `${item.id}-name`,
    message: `Name is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateAuthority: Validator<{ authority?: eTaxAuthorityType | null }> = (item) => {
  const valid = !!item.authority && Object.values(eTaxAuthorityType).includes(item.authority);
  return {
    id: `${item.id}-authority`,
    message: `Authority is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

/*export const validatePaymentPeriod: Validator<{ period?: PaymentPeriod | null }> = item => {
  const valid = !!item.period && Object.values(PaymentPeriod).includes(item.period);
  return {
    id: `${item.id}-period`,
    message: `Payment Period is ${valid ? 'valid' : 'invalid'}`,
    valid
  };
};*/

export const validateOptionalMonths: Validator<{ months?: number | null }> = (item) => {
  const valid = isNil(item.months) || isNumber(item.months);
  return {
    id: `${item.id}-months`,
    message: `Months is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalAmountPerDay: Validator<{ amount_per_day?: number | null }> = (item) => {
  const valid = isNil(item.amount_per_day) || isNumber(item.amount_per_day);
  return {
    id: `${item.id}-amount_per_day`,
    message: `Amount Per Day is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateDateFrom: Validator<{ date_from?: Date | null }> = (item) => {
  const valid = item.date_from instanceof Date && !isNaN(item.date_from.getTime());
  return {
    id: `${item.id}-date_from`,
    message: `From Date is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateDateTo: Validator<{ date_to?: Date | null }> = (item) => {
  const valid = item.date_to instanceof Date && !isNaN(item.date_to.getTime());
  return {
    id: `${item.id}-date_to`,
    message: `To Date is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalDateFrom: Validator<{ date_from?: Date | null }> = (item) => {
  const valid = isNil(item.date_from) || (item.date_from instanceof Date && !isNaN(item.date_from.getTime()));
  return {
    id: `${item.id}-date_from`,
    message: `From Date is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalDateTo: Validator<{ date_to?: Date | null }> = (item) => {
  const valid = isNil(item.date_to) || (item.date_to instanceof Date && !isNaN(item.date_to.getTime()));
  return {
    id: `${item.id}-date_to`,
    message: `To Date is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateOptionalMonthlyPayment: Validator<{ monthly_payment?: number | null }> = (item) => {
  const valid = isNil(item.monthly_payment) || isNumber(item.monthly_payment);
  return {
    id: `${item.id}-monthly_payment`,
    message: `Monthly Payment is ${valid ? 'valid' : 'invalid'}`,
    valid,
  };
};

export const validateAllOrNone =
  (...validators: Array<Validator<{}>>): Validator<{}> =>
  (item) => {
    const validatorResults = validators.map((validator) => validator(item));
    const valid =
      validatorResults.every((result) => !result.valid) ||
      validatorResults.reduce((isValid, result) => isValid && result.valid, true as boolean);
    return {
      id: `${item.id}-all_or_none`,
      items: validatorResults,
      message: `All of these fields must be valid or left empty:`,
      valid,
    };
  };

//Extra fees to make non-removable for Underwriter American Digital
const americanDigitalMovableTitleFees = [
  eTitleFees.lenders_policy,
  eTitleFees.standard_owners_policy,
  eTitleFees.cpl_lender,
  eTitleFees.tieff_lenders_policy,
  eTitleFees.tieff_owners_policy,
];

const americanDigitalMovableEndorsementFees = [eEndorsements._8_1_06, eEndorsements._9_06];

export interface IValidateAmountTotalBaseItem {
  originalTotal?: number | null;
  buyer_at_closing?: number | null;
  buyer_before_closing?: number | null;
  seller_at_closing?: number | null;
  seller_before_closing?: number | null;
  paid_by_others?: number | null;
}

export type IValidateAmountTotalItem =
  | (IValidateAmountTotalBaseItem & { type: eCDV3ItemTypes.title_fee; sub_type?: eTitleFees })
  | (IValidateAmountTotalBaseItem & { type: eCDV3ItemTypes.endorsement; endorsement?: eEndorsements });

function shouldValidateTotal(item: IValidateAmountTotalItem, underwriter?: eUnderwriter) {
  let isAmericanDigitalMovableCDItem =
    (item.type === eCDV3ItemTypes.title_fee &&
      item.sub_type &&
      americanDigitalMovableTitleFees.includes(item.sub_type)) ||
    (item.type === eCDV3ItemTypes.endorsement &&
      item.endorsement &&
      americanDigitalMovableEndorsementFees.includes(item.endorsement));
  // only american digital orders support movable items
  return underwriter === eUnderwriter.AmericanDigital && isAmericanDigitalMovableCDItem;
}

export const validateAmountTotal: Validator<IValidateAmountTotalItem, IOrderDetailsData> = (item, other) => {
  const underwriterParty = other ? other.parties.find((p) => p.purpose === ePartyPurpose.underwriter) : undefined;
  const underwriter =
    underwriterParty && underwriterParty.type === ePartyType.entity
      ? (underwriterParty.entity_value as eUnderwriter)
      : undefined;
  const amounts = [
    item.buyer_at_closing,
    item.buyer_before_closing,
    item.seller_at_closing,
    item.seller_before_closing,
    item.paid_by_others,
  ];
  // If the original value is null or undefined, then we also have no need to do anything here.
  //  Currently, it usually ends up as undefined, but I want to check both as there might be another case where it shows up as null
  const valid =
    item.originalTotal !== null && item.originalTotal !== undefined && shouldValidateTotal(item, underwriter)
      ? (item.originalTotal || 0) === amounts.reduce(sum, 0)
      : true;
  return {
    id: `${item.id}-amount_total`,
    message: (
      <>
        {'Amount Total must add up to '}
        <Currency value={item.originalTotal} />
      </>
    ),
    valid,
  };
};

// Subclasses/Interfaces/Mixins
export const validatorsForSharedProperties = [
  validateType,
  validateId,
  validateOwnerId,
  validateNettedOut,
  validateCreatedAt,
];

export const validatorsForAmountProperties = [
  validateOptionalBuyerAtClosing,
  validateOptionalBuyerBeforeClosing,
  validateOptionalSellerAtClosing,
  validateOptionalSellerBeforeClosing,
  validateOptionalPaidByOthers,
];
